import axios from 'axios';
import _ from 'lodash'
import { ActionTree } from 'vuex';
import { RootState } from '@/store/types';
import {
  GroupFormationState,
  AssignmentStatus,
} from '@/store/groups/formation/types';
import { multipartParser } from '@/store/utils'

const actions: ActionTree<GroupFormationState, RootState> = {
  /**
   * グループ編成の進捗状況を取得
   */
  fetchGroupAssignments: ({ commit, dispatch, rootState }) => {
    return axios.get('/groups/assignment/statuses')
      .then(async ({ data }) => {
        if (_.isEmpty(rootState.master?.semesters)) {
          await dispatch('fetchMasterSemesters')
        }

        const semesters = rootState.master?.semesters
        const assignments: AssignmentStatus[] = _.map(data, (body): AssignmentStatus => {
          const semester = _.find(semesters, { code: body.semesterCode })
          if (_.isUndefined(semester)) {
            throw Error(`Cannot found semester with code: ${body.semesterCode}`)
          }

          return {
            year: body.fiscalYear,
            semester,
            step: body.status,
            name: body.name,
          }
        })
        commit('fetchedGroupAssignments', assignments)
      })
  },
  /**
   * グループ編成：アカデミックカレンダー インポート
   * @param context
   * @param {{
   *    assignment: AssignmentStatus, // グループ編成対象
   *    csv: File, // カレンダーCSV
   * }} param1
   */
  importAcademicCalendarByCsv: (
    context,
    { assignment, csv }: { assignment: AssignmentStatus, csv: File },
  ) => {
    const formData = new FormData()
    formData.append('fiscalYear', String(assignment.year))
    formData.append('semesterCode', assignment.semester.code)
    formData.append('name', assignment.name)
    formData.append('csv', csv)
    return axios.post('/courses/sync/schedules', formData)
  },

  /**
   * グループ編成：アカデミックカレンダー インポート結果取得
   * @param context
   * @param assignment: AssignmentStatus, // グループ編成対象
   */
  checkImportAcademicCalendarByCsv: (
    context,
    assignment: AssignmentStatus,
  ) => {
    return axios.get('/courses/sync/schedules/status', {
      params: {
        ..._.pickBy({
          fiscalYear: assignment.year,
          semesterCode: assignment.semester.code,
          name: assignment.name,
        }),
      },
    })
  },

  /**
   * グループ編成：グループ仮編成
   * @param context
   * @param {{
   *    assignment: AssignmentStatus, // グループ編成対象
   *    tutorCsv, // チューター情報のCSV
   *    studentCsv, // 履修者情報のCSV
   * }} param1
   */
  assignGroupByCsv: (
    context,
    {
      assignment, tutorCsv, studentCsv,
    }: {
      assignment: AssignmentStatus, tutorCsv: File, studentCsv: File
    },
  ): Promise<{
    warnings: string[]|any,
    tutorAssignmentCsv: {
      filename: string,
      charset: string,
      content: Object,
    },
  }> => {
    const formData = new FormData()
    formData.append('fiscalYear', String(assignment.year))
    formData.append('semesterCode', assignment.semester.code)
    formData.append('name', assignment.name)
    formData.append('tutorCsv', tutorCsv)
    formData.append('studentCsv', studentCsv)

    return axios.post('/groups/preCreate', formData)
  },

  /**
   * グループ編成：グループ仮編成結果取得
   * @param context
   * @param assignment: AssignmentStatus, // グループ編成対象
   */
  checkAssignGroupByCsv: (
    context,
    assignment: AssignmentStatus,
  ) => {
    return axios.get('/groups/preCreate/status', {
      params: {
        ..._.pickBy({
          fiscalYear: assignment.year,
          semesterCode: assignment.semester.code,
          name: assignment.name,
        }),
      },
    })
      .then(({ data }) => {
        const responses = multipartParser(data)

        // チューター割付CSV情報取得
        const tutorAssignment = _.chain(responses)
          .filter({ type: 'csv' })
          .last()
          .value()
        const filename = _.chain(tutorAssignment)
          .get('headers')
          .get('Content-Disposition')
          .split(';')
          .reduce((carry, params) => {
            const [key, value] = _.trim(params).split('=')
            return _.set(carry, key, value)
          }, {})
          .get('filename')
          .value()

        return {
          // 警告情報
          warnings: _.chain(responses)
            .filter({ type: 'json' })
            .last()
            .get('body')
            .value(),
          // 仮グループ
          tutorAssignmentCsv: {
            filename,
            charset: tutorAssignment.options.charset || 'utf-8',
            content: tutorAssignment.body,
          },
        }
      })
  },

  /**
   * グループ作成
   *
   * @param context
   * @param {{
   *    assignment: Assignment, // グループ編成対象
   *    tutorAssignmentCsv, // チューターアサインCSV
   * }} param1
   */
  importGroupByCsv: (
    context,
    {
      assignment, tutorAssignmentCsv,
    }: {
      assignment: AssignmentStatus, tutorAssignmentCsv: File
    },
  ): Promise<{warnings: string[]}> => {
    const formData = new FormData()
    formData.append('csv', tutorAssignmentCsv)
    formData.append('fiscalYear', String(assignment.year))
    formData.append('semesterCode', assignment.semester.code)
    formData.append('name', assignment.name)

    return axios.post('/groups/bulkCreate', formData)
      .then(({ data }) => ({
        warnings: data.warning || [],
      }))
  },

  /**
   * グループ編成：グループ作成結果取得
   * @param context
   * @param assignment: AssignmentStatus, // グループ編成対象
   */
  checkImportGroupByCsv: (
    context,
    assignment: AssignmentStatus,
  ) => {
    return axios.get('/groups/bulkCreate/status', {
      params: {
        ..._.pickBy({
          fiscalYear: assignment.year,
          semesterCode: assignment.semester.code,
          name: assignment.name,
        }),
      },
    })
      .then(({ data }) => ({
        warnings: data.warnings || [],
      }))
  },

  /**
   * グループにブースを割り付ける
   *
   * @param context
   * @param {{
   *    assignment: AssignmentStatus, // グループ編成対象
   * }} param1
   */
  assignGroupBooths: (
    context,
    { assignment }: { assignment: AssignmentStatus },
  ) => {
    return axios.put('/groups/assignment/booths', {
      fiscalYear: assignment.year,
      semesterCode: assignment.semester.code,
      name: assignment.name,
    })
  },

  /**
   * グループ編成：ブース割付結果確認
   * @param context
   * @param assignment: AssignmentStatus, // グループ編成対象
   */
  checkAssignGroupBooths: (
    context,
    assignment: AssignmentStatus,
  ) => {
    return axios.get('/groups/assignment/booths/status', {
      params: {
        ..._.pickBy({
          fiscalYear: assignment.year,
          semesterCode: assignment.semester.code,
          name: assignment.name,
        }),
      },
    })
      .then(({ data }) => {
        const responses = multipartParser(data)

        const boothAssignment = _.chain(responses)
          .filter({ type: 'csv' })
          .last()
          .value()
        const filename = _.chain(boothAssignment)
          .get('headers')
          .get('Content-Disposition')
          .split(';')
          .reduce((carry, params) => {
            const [key, value] = _.trim(params).split('=')
            return _.set(carry, key, value)
          }, {})
          .get('filename')
          .value()

        return {
          warnings: _.chain(responses)
            .filter({ type: 'json' })
            .last()
            .get('body')
            .value(),
          boothAssignmentCsv: {
            filename,
            charset: boothAssignment.options.charset || 'utf-8',
            content: boothAssignment.body,
          },
        }
      })
  },

  /**
   * グループ編成：ブース変更手動割当更新
   *
   * @param context
   * @param {{
   *    assignment: Assignment, // ブース割当対象
   *    boothAssignmentCsv, // ブース割当CSV
   * }} param1
   */
  importBoothByCsv: (
    context,
    {
      assignment, boothAssignmentCsv,
    }: {
      assignment: AssignmentStatus, boothAssignmentCsv: File
    },
  ): Promise<{warnings: string[]}> => {
    const formData = new FormData()
    formData.append('csv', boothAssignmentCsv)
    formData.append('fiscalYear', String(assignment.year))
    formData.append('semesterCode', assignment.semester.code)
    formData.append('name', assignment.name)

    return axios.post('/groups/assignment/booths/edit', formData)
      .then(({ data }) => ({
        warnings: data.warning || [],
      }))
  },

  /**
   * グループ編成：ブース変更手動割当しない
   *
   * @param context
   * @param {{
   *    assignment: Assignment, // ブース割当対象
   * }} param1
   */
  importAssignBoothSkip: (
    context,
    {
      assignment,
    }: {
      assignment: AssignmentStatus,
    },
  ): Promise<{warnings: string[]}> => {
    const formData = new FormData()
    formData.append('fiscalYear', String(assignment.year))
    formData.append('semesterCode', assignment.semester.code)
    formData.append('name', assignment.name)

    return axios.post('/groups/assignment/booths/skip', formData)
  },

  /**
   * グループ編成：ブース割付変更結果確認
   * @param context
   * @param assignment: AssignmentStatus, // グループ編成対象
   */
  checkAssignBoothsEdit: (
    context,
    assignment: AssignmentStatus,
  ) => {
    return axios.get('/groups/assignment/booths/edit/status', {
      params: {
        ..._.pickBy({
          fiscalYear: assignment.year,
          semesterCode: assignment.semester.code,
          name: assignment.name,
        }),
      },
    })
      .then(({ data }) => ({
        warnings: data.warnings || [],
      }))
  },

  /**
   * グループをLMSに反映する
   *
   * @param {{
   *    assignment: AssignmentStatus, // グループ編成対象
   * }}
   */
  sendGroupsToLms: (
    context,
    { assignment }: { assignment: AssignmentStatus },
  ) => axios.post('/groups/sectionLinkage', {
    fiscalYear: assignment.year,
    semesterCode: assignment.semester.code,
    name: assignment.name,
  }),

  /**
   * グループ編成：セクション連携結果確認
   * @param context
   * @param assignment: AssignmentStatus, // グループ編成対象
   */
  checkSendGroupsToLms: (
    context,
    assignment: AssignmentStatus,
  ) => {
    return axios.get('/groups/sectionLinkage/status', {
      params: {
        ..._.pickBy({
          fiscalYear: assignment.year,
          semesterCode: assignment.semester.code,
          name: assignment.name,
        }),
      },
    })
  },
};
export default actions;
